import React, { useEffect, useState } from "react";

import {
  ReactComponent as ArrowLeft,
  ReactComponent as ArrowRight,
} from "src/images/chevron-left.svg";
import { ReactComponent as ShareIcon } from "src/images/share-alt.svg";
import publicationPlaceholder from "src/images/eye-placeholder.svg";
import creatorPlaceholder from "src/images/creator-placeholder.svg";

import Modal, { OutsideClickModes } from "src/app/components/Modal/Modal";
import { Button } from "src/app/components/Button/Button";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import moment from "moment";
import OuterLink from "src/app/components/OuterLink/OuterLink";
import { SocialProviderIcon2 } from "src/app/pages/CreatorDiscoveryTool/legacy/app/components/SocialProviderIcon2/SocialProviderIcon2";
import { Img } from "src/app/components/Img/Img";
import YoutubePlayer from "react-player/youtube";
import { TikTokVideo } from "src/app/components/TikTokVideo/TikTokVideo";
import { GalleryCreatorData, SocialProvider } from "src/types";
import { GalleryListElement } from "../GalleryItem/GalleryItem";
import TooltipPortal from "src/app/components/TooltipPortal/TooltipPortal";

import "./GalleryContentViewer.scss";

interface Props {
  data: GalleryListElement[];
  slideIndex: number | null;
  clearSlideIndex: () => void;
  creatorData: GalleryCreatorData;
  keywordsToHighlight?: string[];
}

export default function GalleryContentViewer(props: Props) {
  const {
    data,
    slideIndex,
    clearSlideIndex,
    creatorData,
    keywordsToHighlight,
  } = props;

  const [slideToShow, setSlideToShow] = useState<number | null>(null);

  useEffect(() => {
    setSlideToShow(slideIndex);
  }, [slideIndex]);

  if (slideToShow === null) return null;

  const goToPreviousSlide = () => {
    setSlideToShow(slideToShow - 1);
  };

  const goToNextSlide = () => {
    setSlideToShow(slideToShow + 1);
  };

  const itemToDisplay = data[slideToShow] || {};

  const decodeHTML = (html: string) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    const value = txt.value;

    const keywords =
      itemToDisplay?.keywords && itemToDisplay.keywords.length > 0
        ? itemToDisplay.keywords
        : keywordsToHighlight;

    if (!keywords || keywords.length === 0) {
      return value;
    }

    return highlightKeywords(value, keywords);
  };

  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Strips accents/diacritics and punctuation (apostrophes, spaces, …) so that
  // e.g. the mention handle "lorealparis" can still match the displayed
  // "L'Oréal Paris", which TikTok/IG render with accents/spaces stripped out.
  const foldForMatch = (str: string) =>
    str
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9@#]/g, "");

  const highlightKeywords = (text: string, keywords: string[]) => {
    if (!text || !Array.isArray(keywords) || keywords.length === 0) {
      return text;
    }

    const normalizedKeywords = Array.from(
      new Set(keywords.map((k) => foldForMatch(k)).filter(Boolean)),
    );

    if (normalizedKeywords.length === 0) {
      return text;
    }

    let normalizedText = "";
    const indexMap: number[] = [];

    for (let i = 0; i < text.length; i++) {
      const folded = foldForMatch(text[i]);

      for (const char of folded) {
        normalizedText += char;
        indexMap.push(i);
      }
    }

    const pattern = normalizedKeywords
      .sort((a, b) => b.length - a.length)
      .map((k) => escapeRegex(k))
      .join("|");
    const regex = new RegExp(pattern, "g");

    const matches: { start: number; end: number }[] = [];
    let match: RegExpExecArray | null;

    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(normalizedText)) !== null) {
      const normStart = match.index;
      const normEnd = match.index + match[0].length - 1;

      matches.push({
        start: indexMap[normStart],
        end: indexMap[normEnd] + 1,
      });

      if (match[0].length === 0) {
        regex.lastIndex++;
      }
    }

    if (matches.length === 0) {
      return text;
    }

    matches.sort((a, b) => a.start - b.start);

    const merged: { start: number; end: number }[] = [];

    for (const current of matches) {
      const last = merged[merged.length - 1];

      if (last && current.start <= last.end) {
        last.end = Math.max(last.end, current.end);
      } else {
        merged.push({ ...current });
      }
    }

    let result = "";
    let cursor = 0;

    for (const { start, end } of merged) {
      result += text.slice(cursor, start);
      result += `<span class="highlight">${text.slice(start, end)}</span>`;
      cursor = end;
    }

    result += text.slice(cursor);

    return result;
  };

  const renderPreview = () => {
    if (itemToDisplay.socialProvider === "youtube") {
      return (
        // @ts-ignore
        <YoutubePlayer
          url={itemToDisplay.url}
          playing={1}
          controls={1}
          config={{
            playerVars: { controls: 1 },
          }}
          width="100%"
          height="100%"
        />
      );
    }

    if (["tik_tok", "tiktok"].includes(itemToDisplay.socialProvider)) {
      return (
        <TikTokVideo
          url={itemToDisplay.url}
          fallbackImageSrc={publicationPlaceholder}
        />
      );
    }

    if (
      itemToDisplay.mediaType === "video" &&
      itemToDisplay.socialProvider !== SocialProvider.Snapchat
    ) {
      return (
        <video
          src={itemToDisplay.imageUrl}
          controls
          width="100%"
          height="100%"
        />
      );
    }

    return (
      <Img
        src={itemToDisplay.imageUrl}
        fallbackImageSrc={publicationPlaceholder}
        referrerPolicy="no-referrer"
      />
    );
  };

  const handleClose = () => {
    setSlideToShow(null);
    clearSlideIndex();
  };

  const dynamicCreatorData = itemToDisplay.creatorData || creatorData;

  return (
    <Modal
      className="gallery-content-viewer"
      overlayClassName="gallery-content-viewer__overlay"
      onClose={handleClose}
      outsideClick={OutsideClickModes.Allow}
    >
      <div className="gallery-content-viewer__content" key={slideToShow}>
        {renderPreview()}
      </div>
      <div className="gallery-content-viewer__data">
        <div className="gallery-content-viewer__data-creator">
          <div className="gallery-content-viewer__data-creator-avatar">
            <Img
              src={dynamicCreatorData.profilePictureUrl}
              fallbackImageSrc={creatorPlaceholder}
              referrerPolicy="no-referrer"
            />
            <SocialProviderIcon2 provider={itemToDisplay.socialProvider} />
          </div>

          <div className="gallery-content-viewer__data-creator-name">
            {dynamicCreatorData.username}
            {itemToDisplay.isPaidPost && (
              <TooltipPortal
                className="gallery-content-viewer__data-creator-is-paid-post-tooltip"
                content={
                  itemToDisplay.isPaidPostSource === "native" ? (
                    <IDHFormattedMessage
                      id="ws_is_paid_post_native_tooltip"
                      defaultMessage="Paid partnership - marked by the creator."
                    />
                  ) : (
                    <IDHFormattedMessage
                      id="ws_is_paid_post_tooltip"
                      defaultMessage="Based on the post's content."
                    />
                  )
                }
              >
                <span className="gallery-content-viewer__data-creator-is-paid-post">
                  {itemToDisplay.isPaidPostSource === "native" ? (
                    <IDHFormattedMessage
                      id="ws_is_paid_post_native"
                      defaultMessage="Paid partnership"
                    />
                  ) : (
                    <IDHFormattedMessage
                      id="ws_is_paid_post"
                      defaultMessage="Possible sponsored content"
                    />
                  )}
                </span>
              </TooltipPortal>
            )}
          </div>
          {itemToDisplay?.url?.length > 0 ? (
            <OuterLink href={itemToDisplay.url}>
              <ShareIcon className="share-icon" />
            </OuterLink>
          ) : null}
        </div>
        <div className="gallery-content-viewer__data-description">
          <div className="gallery-content-viewer__data-description-text">
            <p
              dangerouslySetInnerHTML={{
                __html: decodeHTML(itemToDisplay.description),
              }}
            />
          </div>
          <div className="gallery-content-viewer__data-description-date">
            {moment(itemToDisplay.publishedDate)
              .format("DD.MM.YYYY")
              .toString()}
          </div>
        </div>
        <div className="gallery-content-viewer__data-buttons">
          <Button
            variant="white-with-light-grey-border"
            size="large"
            onClick={handleClose}
            dataQa="close-gallery-button"
          >
            <IDHFormattedMessage id="ws_close" defaultMessage="Close" />
          </Button>

          <div className="gallery-content-viewer__data-buttons-arrows">
            <Button
              variant="white-with-light-grey-border"
              size="square"
              disabled={slideToShow === 0}
              onClick={goToPreviousSlide}
              dataQa="gallery-previous-slide-button"
            >
              <ArrowLeft />
            </Button>
            <Button
              className="reversed-button"
              variant="white-with-light-grey-border"
              size="square"
              disabled={slideToShow === data.length - 1}
              onClick={goToNextSlide}
              dataQa="gallery-next-slide-button"
            >
              <ArrowRight />
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
